if (!customElements.get('mobile-sticky-buy-buttons')) {
  customElements.whenDefined('product-form').then(() => {
    class MobileStickyBuyButtons extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section?.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm?.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.addButton = this.querySelector('.mobile-sticky-buy-buttons__add');
        this.selection = this.querySelector('.mobile-sticky-buy-buttons__selection');
        this.selectionValue = this.querySelector('.mobile-sticky-buy-buttons__selection-value');
        this.changeButton = this.querySelector('[data-mobile-sticky-change]');
        this.variantPicker = this.section?.querySelector('variant-picker');
        this.variants = this.parseVariants();
        this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';

        if (!this.section || !this.mainProductForm || !this.mainProductSubmitForm || !this.submitForm || !this.addButton) return;

        this.syncFromMainForm();
        this.boundSyncFromMainForm = this.syncFromMainForm.bind(this);
        this.boundSyncSelectionPreview = this.syncSelectionPreview.bind(this);

        this.mainProductSubmitForm.addEventListener('change', this.boundSyncFromMainForm);
        this.mainProductSubmitForm.addEventListener('input', this.boundSyncFromMainForm);
        this.variantPicker?.addEventListener('change', this.boundSyncSelectionPreview);
        this.section.addEventListener('on:variant:change', this.boundSyncFromMainForm);
        this.changeButton?.addEventListener('click', this.handleChangeClick.bind(this));
      }

      disconnectedCallback() {
        if (this.mainProductSubmitForm && this.boundSyncFromMainForm) {
          this.mainProductSubmitForm.removeEventListener('change', this.boundSyncFromMainForm);
          this.mainProductSubmitForm.removeEventListener('input', this.boundSyncFromMainForm);
        }
        if (this.variantPicker && this.boundSyncSelectionPreview) {
          this.variantPicker.removeEventListener('change', this.boundSyncSelectionPreview);
        }
        if (this.section && this.boundSyncFromMainForm) {
          this.section.removeEventListener('on:variant:change', this.boundSyncFromMainForm);
        }
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('.mobile-sticky-buy-buttons__variants')?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      getSelectedVariantTitle(variantId) {
        if (this.hasOnlyDefaultVariant) return '';
        if (!variantId) return '';
        const selectedVariant = this.variants.find((variant) => String(variant.id) === String(variantId));
        return selectedVariant?.title ? selectedVariant.title.replaceAll(' / ', '-') : '';
      }

      getPendingSelectionTitle() {
        if (this.hasOnlyDefaultVariant) return '';
        if (!this.variantPicker || typeof this.variantPicker.getSelectedOptions !== 'function') return '';
        const selectedOptions = this.variantPicker.getSelectedOptions().filter(Boolean);
        return selectedOptions.length ? selectedOptions.join('-') : '';
      }

      updateSelectionDisplay(title) {
        if (!this.selection || !this.selectionValue) return;

        if (this.hasOnlyDefaultVariant) {
          this.selection.classList.add('is-hidden');
          this.selectionValue.textContent = '';
          return;
        }

        if (title) {
          this.selection.classList.remove('is-hidden');
          this.selectionValue.textContent = title;
        } else {
          this.selection.classList.add('is-hidden');
          this.selectionValue.textContent = '';
        }
      }

      syncSelectionPreview() {
        this.updateSelectionDisplay(this.getPendingSelectionTitle());
      }

      handleChangeClick() {
        const target = this.variantPicker?.closest('.input-row') || this.variantPicker || this.section.querySelector('#product-info');
        if (target) {
          const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
          const rect = target.getBoundingClientRect();
          const targetTop = window.scrollY + rect.top;
          const centeredTop = targetTop - ((window.innerHeight - rect.height) / 2) - (headerHeight / 2);
          window.scrollTo({
            top: Math.max(0, centeredTop),
            behavior: 'smooth'
          });
        }
      }

      syncFromMainForm() {
        const mainVariantInput = this.mainProductSubmitForm.querySelector('[name="id"]');
        const stickyVariantInput = this.submitForm.querySelector('[name="id"]');
        const previousVariantId = stickyVariantInput?.value || '';
        const selectedVariantTitle = this.getSelectedVariantTitle(mainVariantInput?.value);

        if (mainVariantInput && stickyVariantInput) {
          stickyVariantInput.value = mainVariantInput.value;
          if (!previousVariantId && stickyVariantInput.value) {
            stickyVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.submitForm.querySelectorAll('[data-copied="true"]').forEach((el) => el.remove());

        const formData = new FormData(this.mainProductSubmitForm);
        for (const [name, value] of formData.entries()) {
          if (name === 'id') continue;

          const existingInput = this.submitForm.elements.namedItem(name);
          if (existingInput) {
            if (existingInput instanceof RadioNodeList) {
              Array.from(existingInput).forEach((input) => {
                if ('value' in input) {
                  input.checked = input.value === value;
                }
              });
            } else if (existingInput.type === 'checkbox' || existingInput.type === 'radio') {
              existingInput.checked = existingInput.value === value;
            } else {
              existingInput.value = value;
            }
            continue;
          }

          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          input.dataset.copied = 'true';
          this.submitForm.append(input);
        }

        const unavailableText = this.addButton.dataset.unavailableText || '';
        const soldOutText = this.addButton.dataset.soldOutText || unavailableText;
        const addToCartText = this.addButton.dataset.addToCartText || '';
        const isAvailable = this.mainProductSubmitForm.querySelector('.add-to-cart:not([disabled])');
        const hasVariant = !!mainVariantInput?.value;

        this.updateSelectionDisplay(selectedVariantTitle || this.getPendingSelectionTitle());

        if (!hasVariant) {
          this.addButton.disabled = false;
          this.addButton.textContent = unavailableText;
          return;
        }

        if (isAvailable) {
          this.addButton.disabled = false;
          this.addButton.textContent = addToCartText;
        } else {
          this.addButton.disabled = true;
          this.addButton.textContent = soldOutText;
        }
      }

      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.submitForm.querySelector('[name="id"]')?.value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            theme.scrollToRevealElement(target);
          }
          return;
        }

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find((el) => !el.checkValidity());
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        this.syncFromMainForm();
        await super.handleSubmit(evt);
      }
    }

    customElements.define('mobile-sticky-buy-buttons', MobileStickyBuyButtons);
  });
}
